import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOptimizedConversations as useConversations } from '@/hooks/useOptimizedConversations';
import { useToast } from '@/hooks/use-toast';
import { 
  DollarSign, 
  Settings, 
  FileText, 
  MapPin, 
  Calendar, 
  Gauge, 
  Clock, 
  Wrench, 
  Hash, 
  Cog, 
  CheckCircle, 
  Paintbrush, 
  Home, 
  Radio, 
  Activity,
  Shield,
  AlertCircle,
  Zap,
  Search,
  Lock,
  AlertTriangle
} from 'lucide-react';
import { LifeRemainingProgress } from '@/components/ui/life-remaining-progress';
import { WizardFormData } from '@/types/wizard';
import { UniversalMap } from '@/components/maps';
import { getDocumentCategoryName } from '@/utils/documentCategories';
import { SelectedAircraftBadge } from '@/components/wizard/SelectedAircraftBadge';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface SaleWizardDataDisplayProps {
  aircraftDetails?: any;
  fallbackData?: {
    manufacturer?: string;
    model?: string;
    year?: number;
    condition?: string;
    totalFlightHours?: number;
    price?: number;
    location?: string;
    selectedOptions?: any[];
    images?: string[];
    documents?: any[];
    title?: string;
    description?: string;
    media?: any[];
    mediaByCategory?: Record<string, any[]>;
    latitude?: number;
    longitude?: number;
    service_locations?: any[];
    helicopterId?: string;
    is_buy_request?: boolean;
    listing_type?: string;
    helicopterIconUrl?: string;
    listingId?: string; // Added for conversation context
    currentUserId?: string; // Added to check if user is owner
    sellerId?: string; // Added for conversation creation
  };
  hideLocation?: boolean;
  hideHours?: boolean;
  hideSerial?: boolean;
  humanAddress?: string;
  className?: string;
}

const SaleWizardDataDisplay: React.FC<SaleWizardDataDisplayProps> = ({
  aircraftDetails,
  fallbackData,
  hideLocation = false,
  hideHours = false,
  hideSerial = false,
  humanAddress,
  className = ""
}) => {
  // Use aircraft details if available, otherwise fallback to listing fields
  const data = aircraftDetails || fallbackData || {};
  const { createOrGetConversation } = useConversations();
  const { toast } = useToast();
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [localAddress, setLocalAddress] = useState<string | null>(null);
  
  // Helper function to format document categories
  const formatDocumentCategory = (category?: string) => {
    if (!category) return 'General';
    
    const categoryMap: Record<string, string> = {
      'weight_balance': 'Weight & Balance',
      'maintenance_records': 'Maintenance Records',
      'flight_manual': 'Flight Manual',
      'airworthiness_certificate': 'Airworthiness Certificate',
      'registration_certificate': 'Registration Certificate',
      'insurance_documents': 'Insurance Documents',
      'inspection_reports': 'Inspection Reports',
      'modification_records': 'Modification Records',
      'service_bulletins': 'Service Bulletins',
      'warranty_documents': 'Warranty Documents'
    };
    
    return categoryMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Helper function to format buy request condition labels
  const formatBuyRequestCondition = (condition: string): string => {
    const conditionLabels: Record<string, string> = {
      'new': 'New Only',
      'excellent': 'Excellent or Better',
      'good': 'Good or Better',
      'fair': 'Fair or Better',
      'used': 'Any Condition'
    };
    return conditionLabels[condition?.toLowerCase()] || condition;
  };
  
  // Helper function to safely display aircraft data with fallbacks
  const safeDisplay = (value: any, fallback: string = 'Not provided') => {
    return (value !== null && value !== undefined && value !== '') ? value : fallback;
  };
  
  // Parse location string to extract latitude and longitude
  const parseLocationCoordinates = (locationStr?: string) => {
    if (!locationStr) return { latitude: null, longitude: null };
    
    // Check if location contains coordinates (format: "lat, lng" or similar)
    const coordMatch = locationStr.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (coordMatch) {
      return {
        latitude: parseFloat(coordMatch[1]),
        longitude: parseFloat(coordMatch[2])
      };
    }
    return { latitude: null, longitude: null };
  };
  
  const coordinates = parseLocationCoordinates(data.location);
  const latitude = fallbackData?.latitude || coordinates.latitude;
  const longitude = fallbackData?.longitude || coordinates.longitude;

  // Self-resolve a human-readable address from the map pin when neither
  // the parent's humanAddress nor data.location is available.
  useEffect(() => {
    const hasParentAddress = (humanAddress && humanAddress.trim()) || (data.location && String(data.location).trim());
    if (hasParentAddress) return;
    if (!Number.isFinite(latitude as number) || !Number.isFinite(longitude as number)) return;
    let cancelled = false;
    (async () => {
      try {
        const { reverseGeocode } = await import('@/utils/reverseGeocode');
        const result = await reverseGeocode(latitude as number, longitude as number);
        if (!cancelled && result.success) setLocalAddress(result.address);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [humanAddress, data.location, latitude, longitude]);
  
  // Compute effective radius from multiple sources
  const effectiveRadius = 
    typeof aircraftDetails?.operatingRadius === 'number' ? aircraftDetails.operatingRadius :
    (fallbackData as any)?.service_locations?.find((l: any) => l?.isPrimary)?.radius ??
    (fallbackData as any)?.service_locations?.[0]?.radius ??
    (typeof (aircraftDetails as any)?.searchRadius === 'number' ? (aircraftDetails as any)?.searchRadius : undefined);
  
  // Prioritize fetched data over aircraft details metadata
  const photoCount = fallbackData?.media?.length || 
    (aircraftDetails?.photoCategoriesCount || 
      (fallbackData?.images?.length || 0));

  // Prioritize actual fetched documents
  const actualDocuments = fallbackData?.documents || [];
  const documentCount = actualDocuments.length || aircraftDetails?.documentsCount || 0;
  
  const optionsCount = fallbackData?.selectedOptions?.length || aircraftDetails?.selectedOptionsCount || 0;
  
  const hasMainPhoto = fallbackData?.mediaByCategory?.mainPhoto?.length > 0 ||
    (fallbackData?.images && fallbackData.images.length > 0);

  const isBuyRequest = fallbackData?.is_buy_request;

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
          {isBuyRequest ? (
            <>
              <Search className="w-6 h-6 text-primary" />
              Buyer Requirements
            </>
          ) : (
            <>
              <HelicopterSilhouetteIcon className="w-6 h-6" tone="nav" />
              Aircraft Sale Details
            </>
          )}
        </h2>
        <p className="text-muted-foreground">
          {isBuyRequest ? 'What this buyer is looking for' : 'Complete specification and listing information'}
        </p>
      </div>

      {/* Aircraft Overview - Merged Card with Airbnb-style Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelicopterSilhouetteIcon className="w-5 h-5" tone="nav" />
            Aircraft Overview
          </CardTitle>
          {(aircraftDetails?.helicopterId || fallbackData?.helicopterId) && (
            <SelectedAircraftBadge 
              helicopterId={aircraftDetails?.helicopterId || fallbackData?.helicopterId}
              size="md"
              className="mt-2"
            />
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {/* Manufacturer & Model */}
            {(data.manufacturer || data.model) && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                {fallbackData?.helicopterIconUrl ? (
                  <img src={fallbackData.helicopterIconUrl} alt="Aircraft icon" className="w-8 h-8 object-contain flex-shrink-0" />
                ) : (
                  <HelicopterSilhouetteIcon className="w-8 h-8" tone="nav" />
                )}
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Aircraft</div>
                  <div className="font-medium truncate">{`${data.manufacturer || ''} ${data.model || ''}`.trim()}</div>
                </div>
              </div>
            )}

            {/* Year (for sale listings) OR Max Age (for buy requests) */}
            {isBuyRequest ? (
              // For buy requests, show Max Age instead of Year
              (fallbackData as any)?.max_age && (
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Max Age</div>
                    <div className="font-medium">Up to {(fallbackData as any).max_age} years</div>
                  </div>
                </div>
              )
            ) : (
              data.year && (
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Year</div>
                    <div className="font-medium">{data.year}</div>
                  </div>
                </div>
              )
            )}

            {/* Condition */}
            {data.condition && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Gauge className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">
                    {isBuyRequest ? 'Min Condition' : 'Condition'}
                  </div>
                  <div className={`font-medium capitalize ${
                    data.condition === 'excellent' ? 'text-success' : 
                    data.condition === 'good' ? 'text-blue-600 dark:text-blue-400' : 
                    data.condition === 'fair' ? 'text-yellow-600 dark:text-yellow-400' : ''
                  }`}>
                    {isBuyRequest ? formatBuyRequestCondition(data.condition) : data.condition}
                  </div>
                </div>
              </div>
            )}

            {/* Price */}
            {data.price && (
              <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-success/20 bg-success/5">
                <DollarSign className="w-5 h-5 text-success flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">{isBuyRequest ? 'Budget' : 'Asking Price'}</div>
                  <div className="font-bold text-success">${data.price.toLocaleString()}</div>
                </div>
              </div>
            )}

            {/* Total Flight Hours */}
            {(aircraftDetails?.totalTimeSinceNew || aircraftDetails?.totalFlightHours || fallbackData?.totalFlightHours) && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Clock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Total Hours</div>
                  {hideHours ? (
                    <BlurredPrivateData
                      placeholderContent={<span className="font-medium">X,XXX hrs</span>}
                      fieldName="Hours"
                      size="sm"
                      inline
                    />
                  ) : (
                    <div className="font-medium">
                      {(aircraftDetails?.totalTimeSinceNew || aircraftDetails?.totalFlightHours || fallbackData?.totalFlightHours).toLocaleString()} hrs
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Engine Hours - Only show for single engine (twin engine shows individual engine data) */}
            {aircraftDetails?.engineHours && !aircraftDetails?.isTwinEngine && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Wrench className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Engine Hours</div>
                  {hideHours ? (
                    <BlurredPrivateData
                      placeholderContent={<span className="font-medium">X,XXX hrs</span>}
                      fieldName="Hours"
                      size="sm"
                      inline
                    />
                  ) : (
                    <div className="font-medium">{aircraftDetails.engineHours.toLocaleString()} hrs</div>
                  )}
                </div>
              </div>
            )}

            {/* Registration Number - Hide for buy requests */}
            {!isBuyRequest && aircraftDetails?.registrationNumber && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Registration</div>
                  {hideSerial ? (
                    <BlurredPrivateData
                      placeholderContent={<span className="font-medium font-mono text-sm">N-XXXXX</span>}
                      fieldName="Registration"
                      size="sm"
                      inline
                    />
                  ) : (
                    <div className="font-medium font-mono text-sm">{aircraftDetails.registrationNumber}</div>
                  )}
                </div>
              </div>
            )}

            {/* Serial Number - Hide for buy requests */}
            {!isBuyRequest && aircraftDetails?.serialNumber && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Hash className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Serial Number</div>
                  {hideSerial ? (
                    <BlurredPrivateData
                      placeholderContent={<span className="font-medium font-mono text-sm">XXXXXXX</span>}
                      fieldName="Serial Number"
                      size="sm"
                      inline
                    />
                  ) : (
                    <div className="font-medium font-mono text-sm">{aircraftDetails.serialNumber}</div>
                  )}
                </div>
              </div>
            )}

            {/* Engine Type */}
            {aircraftDetails?.engineType && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Cog className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Engine Type</div>
                  <div className="font-medium">{aircraftDetails.engineType}</div>
                </div>
              </div>
            )}

            {/* Engine Cycles - Only show for single engine */}
            {aircraftDetails?.engineCycles && !aircraftDetails?.isTwinEngine && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <Activity className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Engine Cycles</div>
                  <div className="font-medium">{aircraftDetails.engineCycles.toLocaleString()}</div>
                </div>
              </div>
            )}

            {/* Twin Engine Configuration with Details */}
            {!isBuyRequest && aircraftDetails?.isTwinEngine && (
              <div className="col-span-full">
                <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="font-semibold text-primary">Twin Engine Configuration</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Engine 1 */}
                    <div className="p-3 bg-background rounded-lg border">
                      <div className="text-sm font-medium mb-2 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">1</span>
                        Engine 1
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">TTSN</span>
                          <div className="font-medium">
                            {aircraftDetails.engine1Ttsn?.toLocaleString() || '-'} hrs
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Hours</span>
                          <div className="font-medium">
                            {aircraftDetails.engine1Hours?.toLocaleString() || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Cycles</span>
                          <div className="font-medium">
                            {aircraftDetails.engine1Cycles?.toLocaleString() || '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Engine 2 */}
                    <div className="p-3 bg-background rounded-lg border">
                      <div className="text-sm font-medium mb-2 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-secondary text-secondary-foreground text-xs flex items-center justify-center">2</span>
                        Engine 2
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">TTSN</span>
                          <div className="font-medium">
                            {aircraftDetails.engine2Ttsn?.toLocaleString() || '-'} hrs
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Hours</span>
                          <div className="font-medium">
                            {aircraftDetails.engine2Hours?.toLocaleString() || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Cycles</span>
                          <div className="font-medium">
                            {aircraftDetails.engine2Cycles?.toLocaleString() || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Landing Gear */}
            {aircraftDetails?.landingGear && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <HelicopterSilhouetteIcon className="w-5 h-5" tone="nav" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Landing Gear</div>
                  <div className="font-medium">{aircraftDetails.landingGear}</div>
                </div>
              </div>
            )}

            {/* Last Inspection - Hide for buy requests */}
            {!isBuyRequest && aircraftDetails?.lastInspectionDate && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <CheckCircle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Last Inspection</div>
                  <div className="font-medium text-sm">
                    {new Date(aircraftDetails.lastInspectionDate).toLocaleDateString()}
                    {aircraftDetails.lastInspectionType && (
                      <div className="text-xs text-muted-foreground">({aircraftDetails.lastInspectionType})</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Condition Ratings - styled like Component Life Remaining for mobile parity */}
            {(() => {
              // Legacy data is stored 0-100; current wizard stores 0-10. Normalize to 0-10.
              const norm = (v: any) => {
                const n = Number(v);
                if (!isFinite(n)) return 0;
                return n > 10 ? Math.round(n / 10) : Math.round(n);
              };
              return (
                <>
                  {!isBuyRequest && aircraftDetails?.paintCondition && (
                    <LifeRemainingProgress
                      label="Paint"
                      value={norm(aircraftDetails.paintCondition)}
                      max={10}
                      icon={Paintbrush}
                      showPercentage={false}
                    />
                  )}
                  {!isBuyRequest && aircraftDetails?.interiorCondition && (
                    <LifeRemainingProgress
                      label="Interior"
                      value={norm(aircraftDetails.interiorCondition)}
                      max={10}
                      icon={Home}
                      showPercentage={false}
                    />
                  )}
                  {!isBuyRequest && aircraftDetails?.avionicsCondition && (
                    <LifeRemainingProgress
                      label="Avionics"
                      value={norm(aircraftDetails.avionicsCondition)}
                      max={10}
                      icon={Radio}
                      showPercentage={false}
                    />
                  )}
                  {!isBuyRequest && aircraftDetails?.engineCondition && (
                    <LifeRemainingProgress
                      label="Engine"
                      value={norm(aircraftDetails.engineCondition)}
                      max={10}
                      icon={Settings}
                      showPercentage={false}
                    />
                  )}
                </>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Component Life Remaining - Show for sale listings with data */}
      {!isBuyRequest && (
        aircraftDetails?.engineLifeLimit || 
        aircraftDetails?.mainRotorLifeLimit || 
        aircraftDetails?.tailRotorLifeLimit || 
        aircraftDetails?.transmissionLifeLimit
      ) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Component Life Remaining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {/* Engine Life */}
              {aircraftDetails?.engineLifeLimit && aircraftDetails?.engineCurrentHours !== undefined && (
                <LifeRemainingProgress
                  label="Engine Life"
                  value={aircraftDetails.engineLifeRemaining || (aircraftDetails.engineLifeLimit - aircraftDetails.engineCurrentHours)}
                  max={aircraftDetails.engineLifeLimit}
                  subtitle={`${aircraftDetails.engineCurrentHours.toLocaleString()} / ${aircraftDetails.engineLifeLimit.toLocaleString()} hrs`}
                  icon={Cog}
                />
              )}
              
              {/* Main Rotor Life */}
              {aircraftDetails?.mainRotorLifeLimit && aircraftDetails?.mainRotorCurrentHours !== undefined && (
                <LifeRemainingProgress
                  label="Main Rotor Life"
                  value={aircraftDetails.mainRotorLifeRemaining || (aircraftDetails.mainRotorLifeLimit - aircraftDetails.mainRotorCurrentHours)}
                  max={aircraftDetails.mainRotorLifeLimit}
                  subtitle={`${aircraftDetails.mainRotorCurrentHours.toLocaleString()} / ${aircraftDetails.mainRotorLifeLimit.toLocaleString()} hrs`}
                  icon={Activity}
                />
              )}
              
              {/* Tail Rotor Life */}
              {aircraftDetails?.tailRotorLifeLimit && aircraftDetails?.tailRotorCurrentHours !== undefined && (
                <LifeRemainingProgress
                  label="Tail Rotor Life"
                  value={aircraftDetails.tailRotorLifeRemaining || (aircraftDetails.tailRotorLifeLimit - aircraftDetails.tailRotorCurrentHours)}
                  max={aircraftDetails.tailRotorLifeLimit}
                  subtitle={`${aircraftDetails.tailRotorCurrentHours.toLocaleString()} / ${aircraftDetails.tailRotorLifeLimit.toLocaleString()} hrs`}
                  icon={Zap}
                />
              )}
              
              {/* Transmission Life */}
              {aircraftDetails?.transmissionLifeLimit && aircraftDetails?.transmissionCurrentHours !== undefined && (
                <LifeRemainingProgress
                  label="Transmission Life"
                  value={aircraftDetails.transmissionLifeRemaining || (aircraftDetails.transmissionLifeLimit - aircraftDetails.transmissionCurrentHours)}
                  max={aircraftDetails.transmissionLifeLimit}
                  subtitle={`${aircraftDetails.transmissionCurrentHours.toLocaleString()} / ${aircraftDetails.transmissionLifeLimit.toLocaleString()} hrs`}
                  icon={Settings}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Maintenance & Inspection Section */}
      {!isBuyRequest && (
        aircraftDetails?.maintenanceHistory || 
        aircraftDetails?.lastInspectionDate || 
        aircraftDetails?.nextMaintenanceDate ||
        aircraftDetails?.totalLandings ||
        aircraftDetails?.complianceStatus
      ) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Maintenance & Inspection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {aircraftDetails?.lastInspectionDate && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Last Inspection</div>
                  <div className="font-medium">
                    {new Date(aircraftDetails.lastInspectionDate).toLocaleDateString()}
                    {aircraftDetails.lastInspectionType && (
                      <span className="text-muted-foreground ml-2">({aircraftDetails.lastInspectionType})</span>
                    )}
                  </div>
                </div>
              )}
              
              {aircraftDetails?.nextMaintenanceDate && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Next Maintenance Due</div>
                  <div className="font-medium">{new Date(aircraftDetails.nextMaintenanceDate).toLocaleDateString()}</div>
                </div>
              )}
              
              {aircraftDetails?.totalLandings && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Total Landings</div>
                  <div className="font-medium">{aircraftDetails.totalLandings.toLocaleString()}</div>
                </div>
              )}
              
              {aircraftDetails?.complianceStatus && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Compliance Status</div>
                  <div className="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {aircraftDetails.complianceStatus}
                  </div>
                </div>
              )}
              
              {aircraftDetails?.maintenanceHistory && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Maintenance History</div>
                  <div className="bg-muted/30 p-3 rounded-lg">
                    {Array.isArray(aircraftDetails.maintenanceHistory) ? (
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {aircraftDetails.maintenanceHistory.map((record: string, i: number) => (
                          <li key={i}>{record}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm">{aircraftDetails.maintenanceHistory}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Avionics & Systems Section */}
      {!isBuyRequest && (
        aircraftDetails?.gpsType || 
        aircraftDetails?.autopilotSystem || 
        (aircraftDetails?.adsbCompliant !== null && aircraftDetails?.adsbCompliant !== undefined) ||
        aircraftDetails?.avionicsPackage ||
        aircraftDetails?.engineType ||
        aircraftDetails?.landingGear ||
        aircraftDetails?.interiorType
      ) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className="w-5 h-5" />
              Avionics & Systems
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {aircraftDetails?.gpsType && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">GPS Type</div>
                  <div className="font-medium">{aircraftDetails.gpsType}</div>
                </div>
              )}
              
              {aircraftDetails?.autopilotSystem && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Autopilot System</div>
                  <div className="font-medium">{aircraftDetails.autopilotSystem}</div>
                </div>
              )}
              
              {aircraftDetails?.adsbCompliant !== null && aircraftDetails?.adsbCompliant !== undefined && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">ADS-B Compliant</div>
                  <Badge variant={aircraftDetails.adsbCompliant ? "default" : "secondary"} className="flex items-center gap-1">
                    {aircraftDetails.adsbCompliant 
                      ? <><CheckCircle className="w-3 h-3" /> Yes</> 
                      : <><AlertCircle className="w-3 h-3" /> No</>
                    }
                  </Badge>
                </div>
              )}
              
              {aircraftDetails?.avionicsPackage && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Avionics Package</div>
                  <div className="font-medium">{aircraftDetails.avionicsPackage}</div>
                </div>
              )}
              
              {aircraftDetails?.engineType && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Engine Type</div>
                  <div className="font-medium">{aircraftDetails.engineType}</div>
                </div>
              )}
              
              {aircraftDetails?.landingGear && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Landing Gear</div>
                  <div className="font-medium">{aircraftDetails.landingGear}</div>
                </div>
              )}
              
              {aircraftDetails?.interiorType && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Interior Type</div>
                  <div className="font-medium">{aircraftDetails.interiorType}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warranties & Coverage Section */}
      {!isBuyRequest && (
        aircraftDetails?.engineWarranty || 
        aircraftDetails?.airframeWarranty || 
        aircraftDetails?.avionicsWarranty
      ) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Warranties & Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {aircraftDetails?.engineWarranty && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Engine Warranty</div>
                  <div className="font-medium text-green-600 dark:text-green-400">{aircraftDetails.engineWarranty}</div>
                </div>
              )}
              
              {aircraftDetails?.airframeWarranty && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Airframe Warranty</div>
                  <div className="font-medium text-green-600 dark:text-green-400">{aircraftDetails.airframeWarranty}</div>
                </div>
              )}
              
              {aircraftDetails?.avionicsWarranty && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Avionics Warranty</div>
                  <div className="font-medium text-green-600 dark:text-green-400">{aircraftDetails.avionicsWarranty}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Condition & History Section */}
      {!isBuyRequest && (aircraftDetails?.damageHistory || aircraftDetails?.complianceStatus) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Condition & History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {aircraftDetails?.damageHistory && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Damage History</div>
                  <div className="bg-muted/30 p-3 rounded-lg text-sm">
                    {aircraftDetails.damageHistory}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">

        {/* Location & Contact */}
        <Card className="p-6 overflow-hidden">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5" />
            Location & Contact
          </h3>
          <div className="space-y-3 min-w-0">
            <div className="flex justify-between gap-3 min-w-0">
              <span className="text-muted-foreground shrink-0">Location:</span>
              {hideLocation ? (
                <BlurredPrivateData
                  placeholderContent={<span>City, Country</span>}
                  fieldName="Location"
                  size="sm"
                  inline
                />
              ) : (
                <span className="truncate text-right min-w-0 flex-1">{humanAddress || data.location || localAddress || '…'}</span>
              )}
            </div>
            
            {/* Interactive Map */}
            {Number.isFinite(latitude as number) && Number.isFinite(longitude as number) && (
              <div className="mt-4">
                <span className="text-muted-foreground block mb-2">Map Location:</span>
                {hideLocation ? (
                  <BlurredPrivateData
                    placeholderContent={
                      <div className="h-80 w-full bg-muted rounded-lg flex items-center justify-center">
                        <MapPin className="w-12 h-12 text-muted-foreground/50" />
                      </div>
                    }
                    fieldName="Location"
                    size="lg"
                  />
                ) : (
                  <UniversalMap
                    singleMarker={{ 
                      position: { lat: latitude as number, lng: longitude as number },
                      listingType: fallbackData?.listing_type || (fallbackData?.is_buy_request ? 'buy_sale' : 'sale')
                    }}
                    radius={effectiveRadius}
                    height="h-80"
                    className="w-full"
                    assetType="helicopter"
                  />
                )}
              </div>
            )}
            
          </div>
        </Card>

        {/* Documents - Only show for seller listings (not buy requests) */}
        {!fallbackData?.is_buy_request && (
          <Card className="p-6">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" />
              Documentation
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documents:</span>
                <span>{documentCount} uploaded</span>
              </div>
              
              {/* Document List - Show actual fetched documents with access status */}
              {(fallbackData?.documents && fallbackData.documents.length > 0) && (
                <div className="mt-4">
                  <h4 className="font-medium text-sm mb-3">Uploaded Documents:</h4>
                  <div className="space-y-2">
                    {fallbackData.documents.map((doc, index) => {
                      const isOwner = fallbackData?.currentUserId === fallbackData?.sellerId;
                      const isConfidential = doc.is_confidential;
                      
                      return (
                        <div key={doc.id || index} className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium text-foreground">
                              {getDocumentCategoryName(doc.document_category || '')}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1 flex-wrap">
                              {doc.is_required && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                  Required
                                </span>
                              )}
                              {isConfidential && !isOwner && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                  <Lock className="w-3 h-3 inline mr-1" /> Confidential - Request Access
                                </span>
                              )}
                              {isConfidential && isOwner && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                  Confidential
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fallback: Show wizard documents if no fetched documents */}
              {(!fallbackData?.documents || fallbackData.documents.length === 0) && 
               (aircraftDetails?.documents && aircraftDetails.documents.length > 0) && (
                <div className="mt-4">
                  <h4 className="font-medium text-sm mb-3">Wizard Documents:</h4>
                  <div className="space-y-2">
                    {aircraftDetails.documents.map((doc: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/20 rounded text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium">{getDocumentCategoryName(doc.category || '')}</div>
                            <div className="text-xs text-muted-foreground">
                              {doc.isRequired && <span className="text-red-500">Required</span>}
                              {doc.isConfidential && <span className="text-amber-500">{doc.isRequired ? ' • ' : ''}Confidential</span>}
                            </div>
                          </div>
                        </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {doc.verificationStatus === 'verified' && (
                          <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700 dark:text-green-300 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </Badge>
                        )}
                        {doc.verificationStatus === 'pending' && (
                          <Badge variant="secondary" className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-300">
                            <Clock className="w-3 h-3 inline mr-1" /> Pending
                          </Badge>
                        )}
                        {doc.size && (
                          <span className="text-xs text-muted-foreground">
                            {(doc.size / 1024 / 1024).toFixed(1)}MB
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {documentCount > 0 && (
              <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" /> Documentation ready for buyers
              </div>
            )}
            
            {documentCount === 0 && (
              <div className="text-sm text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> No documents uploaded - consider adding maintenance records, certificates
              </div>
            )}
            
            {/* Request Document Access Button - Only show for non-owners with confidential docs */}
            {!isBuyRequest && 
              fallbackData?.currentUserId && 
              fallbackData?.currentUserId !== fallbackData?.sellerId && 
              fallbackData?.documents?.some((doc: any) => doc.is_confidential) && (
              <div className="pt-4 border-t">
                <Button 
                  variant="default" 
                  className="w-full"
                  disabled={requestingAccess}
                  onClick={async () => {
                    if (!fallbackData?.listingId || !fallbackData?.sellerId) {
                      toast({
                        title: 'Error',
                        description: 'Missing listing information',
                        variant: 'destructive'
                      });
                      return;
                    }
                    
                    setRequestingAccess(true);
                    try {
                      const conversationId = await createOrGetConversation(
                        fallbackData.listingId,
                        fallbackData.sellerId,
                        fallbackData.title || 'Helicopter Listing'
                      );
                      
                      if (conversationId) {
                        window.location.href = `/messages?conversation=${conversationId}&openDocRequest=true`;
                      }
                    } catch (error) {
                      console.error('Error starting conversation:', error);
                      toast({
                        title: 'Error',
                        description: 'Failed to start conversation',
                        variant: 'destructive'
                      });
                    } finally {
                      setRequestingAccess(false);
                    }
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {requestingAccess ? 'Opening conversation...' : 'Request Access to Confidential Documents'}
                </Button>
              </div>
            )}
          </div>
        </Card>
        )}
      </div>
    </div>
  );
};

export default SaleWizardDataDisplay;